import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Users, Search, ArrowLeftRight, Trophy, Send, ExternalLink,
  UserPlus, Loader2, CheckCircle2, Clock, AlertTriangle, Lock, CalendarClock,
  Check, X, ChevronRight, Shield, Flag, MapPin, Hash, Pencil,
} from "lucide-react";
import { api, apiErr } from "../lib/api";
import { Flag as CountryFlag } from "../components/Flag";
import { CinematicPortrait } from "../components/CinematicPortrait";
import { useAuth } from "../context/AuthContext";
import { Card, GoldButton, GhostButton, Loader } from "../components/UI";
import { ClubCrest } from "../components/ClubCrest";

const SECTIONS = [
  { key: "hq", label: "Club HQ", icon: Building2 },
  { key: "roster", label: "Roster", icon: Users },
  { key: "find", label: "Find Players", icon: Search },
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight },
  { key: "compete", label: "Compete", icon: Trophy },
];

const STATUS_CHIP = {
  DRAFT: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  SUBMITTED: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  UNDER_REVIEW: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  ACTIVE: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
};

function StatusChip({ status }) {
  return (
    <span data-testid="club-status-chip" className={`inline-flex items-center px-2.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${STATUS_CHIP[status] || "bg-slate-700/30 text-slate-300 border-slate-600"}`}>
      {status}
    </span>
  );
}

function nextStep(club) {
  const r = club.roster;
  if (club.status === "ACTIVE") return { tone: "ok", text: "Your club is approved and competition eligible." };
  if (club.status === "SUBMITTED" || club.status === "UNDER_REVIEW") return { tone: "wait", text: "Federation review pending — no action required right now." };
  if (!r.eligible) return { tone: "todo", text: `Add ${r.min - r.count} more accepted player${r.min - r.count === 1 ? "" : "s"} to reach the minimum and become competition eligible.` };
  if (club.status === "REJECTED") return { tone: "todo", text: "Federation review was rejected. Adjust your roster, then resubmit for review." };
  return { tone: "todo", text: "Roster meets the minimum — submit your club for Federation review." };
}

function PlayerThumb({ p, size = 40 }) {
  return <CinematicPortrait player={p} variant="thumb" className="shrink-0" style={{ width: Math.round(size * 0.82), height: size }} />;
}

// ---------------------------------------------------------------- No-club onboarding
function CreateClubFlow({ onCreated }) {
  const { player } = useAuth();
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => { api.get("/club-requests/mine").then(({ data }) => setRequests(data.requests || [])).catch(() => {}); }, []);
  const pendingReq = requests.find((r) => ["PENDING", "APPROVING"].includes(r.status));

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2 || !city.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/clubs", { name: name.trim(), city: city.trim() });
      toast.success(`Club created — ${data.wsbf_club_id}`);
      onCreated(data.id);
    } catch (e2) { toast.error(apiErr(e2)); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="club-builder-onboarding">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-2">Club Management</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-black uppercase tracking-tight text-slate-50 leading-none">Build Your Club</h1>
      <p className="text-slate-400 mt-2 text-sm sm:text-base">Create your club. Build your roster. Prepare for sanctioned competition.</p>

      {pendingReq && (
        <Card className="p-5 mt-6 border-amber-500/30" data-testid="pending-club-request">
          <div className="flex items-center gap-2 text-amber-300"><Clock className="w-4 h-4" /><span className="font-heading font-bold uppercase tracking-wide text-sm">Club Request Pending Review</span></div>
          <div className="text-sm text-slate-300 mt-2 break-words">“{pendingReq.club_name}” · {pendingReq.country} {pendingReq.city ? `· ${pendingReq.city}` : ""}</div>
          <div className="text-xs text-slate-500 mt-1">An admin will review this request. You can also create a club directly below.</div>
        </Card>
      )}

      <Card className="relative overflow-hidden mt-6">
        <div className="absolute inset-0 mesh-grid opacity-[0.15] pointer-events-none" />
        <div className="relative p-6 sm:p-8">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold mb-1">Step 1</div>
          <h2 className="font-heading text-2xl font-black uppercase tracking-tight text-slate-50">Create Club Identity</h2>
          <p className="text-slate-400 text-sm mt-1">Your club starts as a <b className="text-slate-200">DRAFT</b>. You become the founding owner, then build the roster to {" "}
            <span className="text-[#E5B842]">15</span> players to submit for Federation review.</p>
          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4 mt-6">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">Club Name</label>
              <input data-testid="create-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Athens Arena"
                className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1.5">City</label>
              <input data-testid="create-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Athens"
                className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-3 text-xs text-slate-500">
              <Flag className="w-3.5 h-3.5 text-[#E5B842]" /> Country &amp; Federation are assigned automatically from your player profile{player?.country ? ` (${player.country.name || player.country})` : ""}.
            </div>
            <div className="sm:col-span-2">
              <GoldButton type="submit" disabled={busy || name.trim().length < 2 || !city.trim()} data-testid="create-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />} Create club &amp; become owner
              </GoldButton>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------- Readiness checklist
function Readiness({ club }) {
  const r = club.roster;
  const items = [
    { ok: true, label: "Club created" },
    { ok: !!club.owner, label: "Owner assigned" },
    { ok: r.eligible, label: `Roster minimum (${r.count}/${r.min})`, warn: !r.eligible },
    { ok: club.status === "ACTIVE", label: `Federation review (${club.status === "ACTIVE" ? "approved" : club.status === "SUBMITTED" || club.status === "UNDER_REVIEW" ? "pending" : club.status === "REJECTED" ? "rejected" : "not submitted"})`, warn: club.status === "REJECTED" },
    { ok: club.status === "ACTIVE", label: `Competition eligible (${club.status === "ACTIVE" ? "yes" : "no"})` },
  ];
  return (
    <div className="space-y-2" data-testid="readiness-checklist">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2.5 text-sm">
          {it.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" /> : <div className={`w-4 h-4 rounded-full border shrink-0 ${it.warn ? "border-rose-400" : "border-slate-600"}`} />}
          <span className={it.ok ? "text-slate-200" : it.warn ? "text-rose-300" : "text-slate-400"}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- Roster
function RosterSection({ club, onSelect }) {
  const r = club.roster;
  const members = club.roster_members || [];
  return (
    <div data-testid="section-roster">
      <SectionHead title="Roster" sub="Jersey numbers and roles are official records — managed by the Federation, not editable here." />
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Metric label="Current" value={r.count} testid="roster-current" />
        <Metric label="Minimum" value={r.min} testid="roster-min" />
        <Metric label="Maximum" value={r.max} testid="roster-max" />
      </div>
      <div className="h-2 rounded-full bg-[#1a1a1a] overflow-hidden mb-1">
        <div className="h-full bg-gradient-to-r from-[#8A5A00] to-[#E5B842]" style={{ width: `${Math.min(100, (r.count / r.max) * 100)}%` }} />
      </div>
      <div className={`text-xs font-mono mb-5 ${r.eligible ? "text-emerald-300" : "text-amber-300"}`}>{r.label}</div>

      {members.length === 0 ? (
        <Card className="p-8 text-center" data-testid="roster-empty">
          <Users className="w-7 h-7 text-slate-600 mx-auto mb-3" />
          <div className="font-heading font-bold uppercase tracking-wide text-slate-300">No players yet</div>
          <div className="text-sm text-slate-500 mt-1">Start building your roster by inviting an eligible player.</div>
        </Card>
      ) : (
        <Card className="overflow-hidden" data-testid="roster-table">
          <div className="grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3rem_1fr_9rem_6rem] gap-3 px-4 py-2.5 border-b border-[rgba(212,175,55,0.14)] text-[10px] font-mono uppercase tracking-wider text-slate-500">
            <div>#</div><div>Player</div><div className="hidden sm:block">WSTF ID</div><div className="hidden sm:block">Role</div>
          </div>
          {members.map((m) => (
            <button key={m.id} onClick={() => onSelect(m)} data-testid={`roster-row-${m.wsbf_player_id}`}
              className="w-full text-left grid grid-cols-[2.5rem_1fr_auto] sm:grid-cols-[3rem_1fr_9rem_6rem] gap-3 px-4 py-3 border-b border-[rgba(212,175,55,0.08)] items-center hover:bg-[#161616]" style={{ transition: "background-color 0.12s ease" }}>
              <span className="font-heading font-bold text-[#E5B842]">{m.jersey_number ?? "—"}</span>
              <span className="flex items-center gap-2.5 min-w-0">
                <PlayerThumb p={m} size={34} />
                <span className="min-w-0">
                  <span className="block text-slate-100 font-semibold truncate">{m.display_name}</span>
                  <span className="block sm:hidden text-[10px] font-mono text-slate-500">{m.wsbf_player_id}</span>
                </span>
              </span>
              <span className="hidden sm:block font-mono text-xs text-slate-400">{m.wsbf_player_id}</span>
              <span className="hidden sm:block text-xs text-slate-400 uppercase tracking-wide">{m.position || "—"}</span>
            </button>
          ))}
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Find Players
function FindSection({ club, onSelect, invited, onInvite }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingInvites, setPendingInvites] = useState([]);
  const atCapacity = club.roster.count >= club.roster.max;
  const timer = useRef();
  const pendingIds = new Set((pendingInvites || []).filter((i) => i.status === "PENDING").map((i) => i.player_id));

  useEffect(() => {
    api.get(`/clubs/${club.id}/invitations`)
      .then(({ data }) => setPendingInvites(data.invitations || []))
      .catch(() => setPendingInvites([]));
  }, [club.id, invited]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q.trim()) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`); setResults(data.players || []); }
      catch { setResults([]); } finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [q]);

  const rosterIds = new Set((club.roster_members || []).map((m) => m.id));
  const pending = (pendingInvites || []).filter((i) => i.status === "PENDING");

  return (
    <div data-testid="section-find">
      <SectionHead title="Find Players" sub="Search the WSTF Player Registry by name or WSTF ID. Inviting a player requires their acceptance." />
      {atCapacity && <Card className="p-3 mb-4 border-amber-500/30 text-xs text-amber-300 flex items-center gap-2" data-testid="roster-at-capacity"><Lock className="w-4 h-4" /> Roster at maximum capacity ({club.roster.max}). Release a player via Transfers before inviting more.</Card>}
      <div className="relative mb-5">
        <Search className="w-4 h-4 text-[#E5B842]/70 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input data-testid="find-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Player name or WSTF-GRE-000001"
          className="w-full bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-xl pl-11 pr-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
      </div>
      {loading ? <Loader label="Searching registry…" /> : results === null ? (
        <div className="text-sm text-slate-500 font-mono">Type a name or WSTF ID to discover players.</div>
      ) : results.length === 0 ? (
        <Card className="p-8 text-center text-sm text-slate-500" data-testid="find-empty">No players match “{q}”.</Card>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="find-results">
          {results.map((p) => {
            const onRoster = rosterIds.has(p.id);
            const contracted = !!p.club_id && !onRoster;
            const isInvited = invited.has(p.id) || pendingIds.has(p.id);
            return (
              <Card key={p.id} className="p-4" data-testid={`find-card-${p.wsbf_player_id}`}>
                <div className="flex items-start gap-3">
                  <PlayerThumb p={p} size={44} />
                  <div className="min-w-0 flex-1">
                    <div className="font-heading font-bold uppercase tracking-tight text-slate-50 truncate">{p.display_name}</div>
                    <div className="text-[11px] font-mono text-slate-400 mt-0.5 break-words">{p.country && <CountryFlag country={p.country} className="inline-block mr-1 align-middle" />}{p.country?.name || ""} · {p.wsbf_player_id}</div>
                    <div className="text-[11px] mt-1">
                      {onRoster ? <span className="text-emerald-300">In your roster</span>
                        : contracted ? <span className="text-amber-300">Under contract · transfer required</span>
                        : <span className="text-orange-300">Free agent</span>}
                      {typeof p.rating === "number" && <span className="text-slate-500"> · Rating {p.rating}</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <GhostButton className="!py-1.5 !px-3 text-xs" onClick={() => onSelect(p)} data-testid={`find-view-${p.wsbf_player_id}`}>View passport</GhostButton>
                  {!onRoster && !contracted && (
                    isInvited
                      ? <span className="inline-flex items-center gap-1.5 text-xs text-amber-300 px-3 py-1.5" data-testid={`invited-${p.wsbf_player_id}`}><Clock className="w-3.5 h-3.5" /> Invitation sent</span>
                      : <GoldButton className="!py-1.5 !px-3 text-xs" disabled={atCapacity} onClick={() => onInvite(p)} data-testid={`find-invite-${p.wsbf_player_id}`}><UserPlus className="w-3.5 h-3.5" /> Invite player</GoldButton>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="mt-8" data-testid="pending-invitations">
        <GroupLabel>Pending Invitations</GroupLabel>
        {pending.length === 0 ? (
          <Empty2 label="No outstanding invitations." />
        ) : (
          <Card className="divide-y divide-[rgba(212,175,55,0.1)]">
            {pending.map((i) => (
              <div key={i.id} className="flex items-center justify-between gap-3 p-3" data-testid={`pending-invite-${i.player_wsbf_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-100 truncate">{i.player_name}</div>
                  <div className="text-[11px] font-mono text-slate-500">{i.player_wsbf_id} · sent {i.sent_at ? new Date(i.sent_at).toLocaleDateString() : "—"}</div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">Pending</span>
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- Transfers
const TX_TONE = { AWAITING_CLUB_DECISION: "text-amber-300", AWAITING_FEDERATION_APPROVAL: "text-[#F0C72A]", APPROVED: "text-emerald-300", COMPLETED: "text-emerald-400", REJECTED: "text-rose-400", CANCELLED: "text-slate-400" };

function TransfersSection({ club }) {
  const [win, setWin] = useState(null);
  const [reqs, setReqs] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get("/transfers/window-status").then(({ data }) => setWin(data)).catch(() => {});
    api.get(`/transfers/club/${club.id}`).then(({ data }) => setReqs(data.requests || [])).catch(() => setReqs([]));
  }, [club.id]);
  useEffect(() => { load(); }, [load]);

  const decide = async (r, decision) => {
    if (!window.confirm(`${decision === "approve" ? "Approve" : "Reject"} this transfer request?`)) return;
    setBusy(r.transfer_request_id);
    try { await api.post(`/transfers/requests/${r.transfer_request_id}/club-decision`, { decision }); toast.success(`Request ${decision}d`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };

  const reopens = win?.next_window ? new Date(win.next_window.opens).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  const closes = win?.current_window ? new Date(win.current_window.closes).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null;
  const decisions = (reqs || []).filter((r) => r.status === "AWAITING_CLUB_DECISION" && r.current_club_id === club.id);
  const fed = (reqs || []).filter((r) => r.status === "AWAITING_FEDERATION_APPROVAL" || r.status === "ELIGIBLE_FOR_RELEASE");

  return (
    <div data-testid="section-transfers">
      <SectionHead title="Transfers" sub="Transfer activity runs through the WSTF Transfer Engine. Final settlement is Federation-controlled." />
      {win && (
        win.is_open
          ? <Card className="p-3 mb-5 border-emerald-500/30 flex items-center gap-2 text-xs text-emerald-200" data-testid="transfer-window-banner"><CalendarClock className="w-4 h-4 text-emerald-300" /> <b className="font-heading uppercase tracking-wide">Transfer Window Open</b>{closes ? ` · closes ${closes}` : ""}</Card>
          : <Card className="p-3 mb-5 border-amber-500/30 flex items-center gap-2 text-xs text-amber-200" data-testid="transfer-window-banner"><Lock className="w-4 h-4 text-amber-300" /> <b className="font-heading uppercase tracking-wide">Transfer Window Closed</b>{reopens ? ` — reopens ${reopens}` : ""}. Free-agent signings &amp; releases continue year-round.</Card>
      )}

      {reqs === null ? <Loader label="Loading transfers…" /> : (
        <>
          <GroupLabel>Needs your decision</GroupLabel>
          {decisions.length === 0 ? <Empty2 label="No transfer requests awaiting your decision." /> : (
            <div className="space-y-2 mb-6">{decisions.map((r) => (
              <Card key={r.transfer_request_id} className="p-4" data-testid={`tx-decision-${r.transfer_request_id}`}>
                <TxLine r={r} />
                <div className="flex gap-2 mt-2">
                  <button disabled={busy === r.transfer_request_id} onClick={() => decide(r, "approve")} data-testid={`tx-approve-${r.transfer_request_id}`} className="inline-flex items-center gap-1 text-xs text-emerald-300 hover:text-emerald-200 disabled:opacity-40"><Check className="w-3.5 h-3.5" /> Approve</button>
                  <button disabled={busy === r.transfer_request_id} onClick={() => decide(r, "reject")} data-testid={`tx-reject-${r.transfer_request_id}`} className="inline-flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200 disabled:opacity-40"><X className="w-3.5 h-3.5" /> Reject</button>
                </div>
              </Card>
            ))}</div>
          )}
          <GroupLabel>Pending Federation</GroupLabel>
          {fed.length === 0 ? <Empty2 label="No requests pending Federation approval." /> : (
            <div className="space-y-2">{fed.map((r) => (
              <Card key={r.transfer_request_id} className="p-4" data-testid={`tx-fed-${r.transfer_request_id}`}><TxLine r={r} /><div className="text-[11px] text-slate-500 mt-1">Read-only — Federation decision required.</div></Card>
            ))}</div>
          )}
        </>
      )}
    </div>
  );
}
function TxLine({ r }) {
  return (
    <div className="text-xs font-mono text-slate-400 space-y-0.5">
      <div className="text-slate-200 font-bold uppercase tracking-wide">{r.request_type}</div>
      <div>Status: <span className={TX_TONE[r.status] || "text-slate-200"}>{r.status}</span></div>
      <div>Club consent: {r.club_consent_status} · Federation: {r.federation_status}</div>
    </div>
  );
}

// ---------------------------------------------------------------- Compete
function CompeteSection({ club, onSubmit, submitting }) {
  const r = club.roster;
  const canSubmit = r.eligible && ["DRAFT", "REJECTED"].includes(club.status);
  const step = nextStep(club);
  const rejected = club.status === "REJECTED";
  const reason = club.federation_review?.status === "REJECTED" ? club.federation_review?.reason : null;
  return (
    <div data-testid="section-compete">
      <SectionHead title="Competition Readiness" sub="Is your club ready to compete? Submit to the Federation when your roster meets the minimum." />
      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <Metric label="Official Status" value={club.status} testid="compete-status" />
        <Metric label="Roster" value={`${r.count} / ${r.max}`} testid="compete-roster" />
        <Metric label="Minimum Required" value={r.min} testid="compete-min" />
        <Metric label="Competition Eligible" value={club.status === "ACTIVE" ? "YES" : "NO"} testid="compete-eligible" />
      </div>
      {club.status === "ACTIVE" ? (
        <CompetitionEntry club={club} />
      ) : (
      <>
      {rejected ? (
        <Card className="p-4 mb-5 border-rose-500/40" data-testid="compete-rejected">
          <div className="flex items-center gap-2 text-rose-300"><AlertTriangle className="w-4 h-4" /><span className="font-heading font-bold uppercase tracking-wide text-sm">Federation Review — Rejected</span></div>
          {reason
            ? <div className="text-sm text-slate-200 mt-2 break-words" data-testid="compete-reject-reason">{reason}</div>
            : <div className="text-sm text-slate-400 mt-2">No reason was provided by the Federation.</div>}
          <div className="text-xs text-slate-500 mt-2">Adjust your roster (minimum {r.min} accepted players), then resubmit for review.</div>
        </Card>
      ) : (
        <Card className="p-4 mb-5" data-testid="compete-fed-state">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1">Federation Review</div>
          <div className="text-sm text-slate-200">
            {club.status === "SUBMITTED" || club.status === "UNDER_REVIEW" ? "Pending Federation review" : "Not yet submitted"}
          </div>
          <div className="text-xs text-slate-500 mt-2">Your club must be ACTIVE (federation-approved) before it can enter competitions.</div>
        </Card>
      )}
      <Card className={`p-4 mb-5 ${step.tone === "ok" ? "border-emerald-500/30" : step.tone === "wait" ? "border-amber-500/30" : "border-[rgba(212,175,55,0.3)]"}`} data-testid="compete-next-step">
        <div className="text-[11px] uppercase tracking-wider text-[#E5B842] font-mono mb-1">Next Step</div>
        <div className="text-sm text-slate-200">{step.text}</div>
      </Card>
      <GoldButton disabled={!canSubmit || submitting} onClick={onSubmit} data-testid="compete-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit for Federation review
      </GoldButton>
      {!canSubmit && !(club.status === "SUBMITTED" || club.status === "UNDER_REVIEW") && (
        <span className="text-xs text-slate-500 ml-3">{r.eligible ? "" : `Need ${r.min - r.count} more players to submit.`}</span>
      )}
      </>
      )}
    </div>
  );
}

const ENTRY_TONE = {
  PENDING: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  APPROVED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  REJECTED: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

function CompetitionEntry({ club }) {
  const [open, setOpen] = useState(null);
  const [mine, setMine] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => {
    api.get("/competitions/open").then(({ data }) => setOpen(data.competitions || [])).catch(() => setOpen([]));
    api.get("/competitions/entry-requests/mine").then(({ data }) => setMine(data.requests || [])).catch(() => setMine([]));
  }, []);
  useEffect(() => { load(); }, [load, club.id]);

  const stateFor = (comp) => (comp.clubs_state || []).find((x) => x.club_id === club.id)?.state || "NOT_ENTERED";
  const request = async (comp) => {
    if (!window.confirm(`Request entry of ${club.name} into ${comp.name}?`)) return;
    setBusy(comp.id);
    try { await api.post(`/competitions/${comp.id}/entry-requests`, { club_id: club.id }); toast.success("Entry request submitted — awaiting admin review."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const cancel = async (rq) => {
    setBusy(rq.id);
    try { await api.post(`/competitions/entry-requests/${rq.id}/cancel`); toast.success("Request cancelled."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const myForClub = mine.filter((r) => r.club_id === club.id);

  return (
    <div className="mt-2" data-testid="compete-entry">
      <GroupLabel>Open Competitions</GroupLabel>
      {open === null ? <Loader label="Loading competitions…" />
        : open.length === 0 ? <Empty2 label="No competitions are open for entry right now." />
        : (
          <div className="space-y-2 mb-6">
            {open.map((comp) => {
              const st = stateFor(comp);
              return (
                <Card key={comp.id} className="p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`open-comp-${comp.id}`}>
                  <div className="min-w-0">
                    <div className="font-heading font-bold uppercase tracking-tight text-slate-50 break-words">{comp.name}</div>
                    <div className="text-[11px] font-mono text-slate-500">{comp.season_label} · {comp.club_count} club{comp.club_count === 1 ? "" : "s"}</div>
                  </div>
                  {st === "ENTERED" ? <span className="text-xs text-emerald-300 font-mono uppercase px-3 py-1.5 rounded bg-emerald-500/10 border border-emerald-500/30" data-testid={`entry-state-${comp.id}`}>Entered</span>
                    : st === "REQUEST_PENDING" ? <span className="text-xs text-amber-300 font-mono uppercase px-3 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 inline-flex items-center gap-1.5" data-testid={`entry-state-${comp.id}`}><Clock className="w-3.5 h-3.5" /> Entry pending</span>
                    : <GoldButton className="!py-1.5 !px-3 text-xs" disabled={busy === comp.id} onClick={() => request(comp)} data-testid={`request-entry-${comp.id}`}>{busy === comp.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Request entry</GoldButton>}
                </Card>
              );
            })}
          </div>
        )}
      <GroupLabel>My Entry Requests</GroupLabel>
      {myForClub.length === 0 ? <Empty2 label="No entry requests yet." />
        : (
          <Card className="divide-y divide-[rgba(212,175,55,0.1)]" data-testid="my-entry-requests">
            {myForClub.map((rq) => (
              <div key={rq.id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`my-entry-${rq.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-slate-100 break-words">{rq.competition?.name}</div>
                  <div className="text-[11px] font-mono text-slate-500">{rq.competition?.season_label}</div>
                  {rq.status === "REJECTED" && rq.reason && <div className="text-[11px] text-rose-300 mt-1 break-words" data-testid={`my-entry-reason-${rq.id}`}>Reason: {rq.reason}</div>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border ${ENTRY_TONE[rq.status] || ""}`}>{rq.status}</span>
                  {rq.status === "PENDING" && <GhostButton className="!py-1 !px-2 text-xs" disabled={busy === rq.id} onClick={() => cancel(rq)} data-testid={`cancel-entry-${rq.id}`}>Cancel</GhostButton>}
                </div>
              </div>
            ))}
          </Card>
        )}
    </div>
  );
}

// ---------------------------------------------------------------- Shared bits
function SectionHead({ title, sub }) {
  return <div className="mb-5"><h2 className="font-heading text-2xl font-black uppercase tracking-tight text-slate-50">{title}</h2>{sub && <p className="text-slate-400 text-sm mt-1 max-w-2xl">{sub}</p>}</div>;
}
function Metric({ label, value, testid }) {
  return <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.2)] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-mono">{label}</div><div className="font-heading text-2xl font-black text-slate-50 mt-1 tabular-nums uppercase" data-testid={testid}>{value}</div></div>;
}
function GroupLabel({ children }) { return <div className="text-[11px] uppercase tracking-[0.22em] text-[#E5B842] font-mono font-bold mb-2">{children}</div>; }
function Empty2({ label }) { return <div className="text-sm text-slate-500 font-mono mb-6 px-1">{label}</div>; }

// ---------------------------------------------------------------- Player context panel
function PlayerPanel({ player, club, invited, onInvite, onClose }) {
  const [hist, setHist] = useState(null);
  useEffect(() => {
    setHist(null);
    if (player?.wsbf_player_id || player?.id) {
      api.get(`/transfers/player/${player.wsbf_player_id || player.id}/summary`).then(({ data }) => setHist(data)).catch(() => setHist(null));
    }
  }, [player]);
  if (!player) return (
    <Card className="p-5" data-testid="player-panel-empty"><div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-2">Player Detail</div><div className="text-sm text-slate-500">Select a player from the roster or search to see details here.</div></Card>
  );
  const onRoster = (club.roster_members || []).some((m) => m.id === player.id);
  const contracted = !!player.club_id && !onRoster;
  const isInvited = invited.has(player.id);
  return (
    <Card className="p-5" data-testid="player-panel">
      <div className="flex items-start gap-3">
        <PlayerThumb p={player} size={48} />
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold uppercase tracking-tight text-slate-50 truncate">{player.display_name}</div>
          <div className="text-[11px] font-mono text-slate-400 break-words">{player.country && <CountryFlag country={player.country} className="inline-block mr-1 align-middle" />}{player.country?.name || ""} · {player.wsbf_player_id}</div>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-300" data-testid="player-panel-close"><X className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4 text-xs">
        <Field label="Status" value={onRoster ? "In your roster" : contracted ? "Under contract" : "Free agent"} />
        <Field label="Rating" value={typeof player.rating === "number" ? player.rating : "—"} />
        <Field label="World Rank" value={player.ranking?.world ?? player.ranking ?? "—"} />
        <Field label="Current Club" value={hist?.current_club?.name || (contracted ? "—" : "Free agent")} />
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Link to={`/player/${player.wsbf_player_id}`} data-testid="panel-view-passport"><GhostButton className="!py-1.5 !px-3 text-xs"><ExternalLink className="w-3.5 h-3.5" /> View passport</GhostButton></Link>
        {!onRoster && !contracted && (
          isInvited ? <span className="inline-flex items-center gap-1.5 text-xs text-amber-300 px-3 py-1.5"><Clock className="w-3.5 h-3.5" /> Invitation sent</span>
            : <GoldButton className="!py-1.5 !px-3 text-xs" onClick={() => onInvite(player)} data-testid="panel-invite"><UserPlus className="w-3.5 h-3.5" /> Invite player</GoldButton>
        )}
        {contracted && <span className="text-xs text-amber-300 px-1 py-1.5">Transfer required — invitation cannot pull a contracted player.</span>}
      </div>
    </Card>
  );
}
function Field({ label, value }) { return <div className="min-w-0"><div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</div><div className="text-slate-200 break-words">{value}</div></div>; }

// ---------------------------------------------------------------- Identity editing (18B-2)
function ColorField({ label, value, onChange, testid }) {
  const safe = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000";
  return (
    <div className="min-w-0">
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">{label}</label>
      <div className="flex items-center gap-2 mt-1">
        <input type="color" value={safe} onChange={(e) => onChange(e.target.value.toUpperCase())} data-testid={`${testid}-picker`}
          className="w-9 h-9 rounded bg-transparent border border-[rgba(212,175,55,0.22)] cursor-pointer shrink-0 p-0.5" />
        <input value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} data-testid={testid} maxLength={7} placeholder="#RRGGBB"
          className="w-full min-w-0 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-2 py-2 text-xs font-mono text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
      </div>
    </div>
  );
}

function NameChangeModal({ club, onClose, onDone }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (name.trim().length < 3) { toast.error("Club name must be at least 3 characters."); return; }
    setBusy(true);
    try {
      await api.post(`/clubs/${club.id}/name-change-requests`, { new_name: name.trim() });
      toast.success("Name-change request submitted for review.");
      onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="name-change-modal" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.25)] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-black uppercase tracking-tight text-slate-50">Request Name Change</h3>
        <p className="text-sm text-slate-400 mt-1 break-words">Current name: “{club.name}”. A WSTF admin must approve before it applies — your club ID, roster and history stay the same.</p>
        <input data-testid="name-change-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="New club name" maxLength={60}
          className="w-full mt-4 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        <div className="flex justify-end gap-2 mt-4">
          <GhostButton onClick={onClose} data-testid="name-change-cancel">Cancel</GhostButton>
          <GoldButton onClick={submit} disabled={busy || name.trim().length < 3} data-testid="name-change-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Submit request</GoldButton>
        </div>
      </div>
    </div>
  );
}

function IdentityEditor({ club, onReload }) {
  const [city, setCity] = useState(club.city || "");
  const [primary, setPrimary] = useState(club.color_primary || "#E5B842");
  const [secondary, setSecondary] = useState(club.color_secondary || "#0D1424");
  const [saving, setSaving] = useState(false);
  const [nameReqs, setNameReqs] = useState([]);
  const [showName, setShowName] = useState(false);

  useEffect(() => {
    setCity(club.city || "");
    setPrimary(club.color_primary || "#E5B842");
    setSecondary(club.color_secondary || "#0D1424");
  }, [club.id, club.city, club.color_primary, club.color_secondary]);

  const loadNameReqs = useCallback(() => {
    api.get(`/clubs/${club.id}/name-change-requests/mine`)
      .then(({ data }) => setNameReqs(data.requests || []))
      .catch(() => setNameReqs([]));
  }, [club.id]);
  useEffect(() => { loadNameReqs(); }, [loadNameReqs]);

  const pendingName = nameReqs.find((n) => n.status === "PENDING");
  const lastRejected = !pendingName ? nameReqs.find((n) => n.status === "REJECTED") : null;
  const hex = /^#[0-9A-Fa-f]{6}$/;
  const dirty = city.trim() !== (club.city || "") || primary !== (club.color_primary || "#E5B842") || secondary !== (club.color_secondary || "#0D1424");
  const valid = city.trim().length >= 1 && city.trim().length <= 60 && hex.test(primary) && hex.test(secondary);
  const previewClub = { ...club, color_primary: primary, color_secondary: secondary };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/clubs/${club.id}`, { city: city.trim(), color_primary: primary, color_secondary: secondary });
      toast.success("Club identity updated.");
      onReload();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Card className="p-5 mb-5" data-testid="hq-identity">
      <GroupLabel>Club Identity</GroupLabel>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-1 mb-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Club Name</div>
          <div className="font-heading font-bold uppercase tracking-tight text-slate-50 break-words" data-testid="identity-name">{club.name}</div>
        </div>
        <GhostButton className="!py-1.5 !px-3 text-xs" onClick={() => setShowName(true)} data-testid="request-name-change-btn"><Pencil className="w-3.5 h-3.5" /> Request name change</GhostButton>
      </div>
      {pendingName && (
        <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200 break-words" data-testid="name-change-pending">
          <span className="font-bold uppercase tracking-wide">Name change pending</span> — “{pendingName.new_name}” awaiting review.
        </div>
      )}
      {lastRejected && (
        <div className="mb-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-200 break-words" data-testid="name-change-rejected">
          Last name-change request for “{lastRejected.new_name}” was rejected.{lastRejected.reject_reason ? ` Reason: ${lastRejected.reject_reason}` : ""}
        </div>
      )}
      <div className="flex items-center gap-4 mb-4">
        <ClubCrest club={previewClub} size={56} />
        <div className="text-xs text-slate-400">Crest preview updates live as you change colours.</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="min-w-0">
          <label className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">City</label>
          <input data-testid="identity-city" value={city} onChange={(e) => setCity(e.target.value)} maxLength={60} placeholder="City"
            className="w-full mt-1 bg-[#0a0a0a] border border-[rgba(212,175,55,0.22)] rounded-lg px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E5B842]/60" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <ColorField label="Primary" testid="identity-color-primary" value={primary} onChange={setPrimary} />
          <ColorField label="Secondary" testid="identity-color-secondary" value={secondary} onChange={setSecondary} />
        </div>
      </div>
      <div className="flex justify-end mt-4">
        <GoldButton className="!py-2 text-xs" disabled={!dirty || !valid || saving} onClick={save} data-testid="identity-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save identity</GoldButton>
      </div>
      {showName && <NameChangeModal club={club} onClose={() => setShowName(false)} onDone={() => { setShowName(false); loadNameReqs(); }} />}
    </Card>
  );
}

// ---------------------------------------------------------------- Main
export default function ClubBuilder() {
  const { user, loading: authLoading, refresh } = useAuth();
  const nav = useNavigate();
  const [clubs, setClubs] = useState(null);
  const [idx, setIdx] = useState(0);
  const [section, setSection] = useState("hq");
  const [selected, setSelected] = useState(null);
  const [invited, setInvited] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(() => {
    api.get("/clubs/mine/owned").then(({ data }) => setClubs(data.clubs || [])).catch(() => setClubs([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (authLoading || clubs === null) return <Loader label="Loading your workspace…" />;
  if (!user) { nav("/login", { replace: true }); return null; }

  const club = clubs[idx];

  const invite = async (p) => {
    try {
      await api.post(`/clubs/${club.id}/invite`, { player_id: p.id });
      toast.success(`Invitation sent to ${p.display_name} — waiting for acceptance.`);
      setInvited((s) => new Set(s).add(p.id));
    } catch (e) { toast.error(apiErr(e)); }
  };
  const submit = async () => {
    setSubmitting(true);
    try { await api.post(`/clubs/${club.id}/submit`); toast.success("Submitted for Federation review."); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  if (clubs.length === 0) {
    return <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-8 sm:py-12 animate-fade-up"><CreateClubFlow onCreated={async (id) => { await refresh(); load(); }} /></div>;
  }

  const r = club.roster;
  const step = nextStep(club);

  const renderRail = (prefix) => (
    <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-1 lg:pb-0" data-testid={`builder-rail-${prefix}`}>
      {SECTIONS.map((s) => {
        const Icon = s.icon; const on = section === s.key;
        return (
          <button key={s.key} onClick={() => setSection(s.key)} data-testid={`rail-${prefix}-${s.key}`}
            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-heading font-bold uppercase tracking-wide whitespace-nowrap shrink-0 ${on ? "bg-[#E5B842] text-black" : "text-slate-300 hover:text-[#E5B842] hover:bg-[#161616]"}`}
            style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
            <Icon className="w-4 h-4 shrink-0" /> {s.label}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 animate-fade-up overflow-x-hidden" data-testid="club-builder">
      {/* HEADER */}
      <div className="relative overflow-hidden rounded-2xl foil-border bg-gradient-to-b from-[#161616] to-[#070707] mb-5" data-testid="builder-header">
        <div className="absolute inset-0 mesh-grid opacity-[0.18] pointer-events-none" />
        <div className="relative p-5 sm:p-7">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#E5B842] font-mono font-bold">Club Management · Owner Workspace</div>
            {clubs.length > 1 && (
              <select value={idx} onChange={(e) => { setIdx(+e.target.value); setSelected(null); setSection("hq"); }} data-testid="club-switcher"
                className="bg-[#0a0a0a] border border-[rgba(212,175,55,0.3)] rounded-lg px-3 py-1.5 text-xs text-slate-200">
                {clubs.map((c, i) => <option key={c.id} value={i}>{c.name}</option>)}
              </select>
            )}
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mt-3">
            <ClubCrest club={club} size={64} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none break-words">{club.name}</h1>
                <StatusChip status={club.status} />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs font-mono text-slate-400">
                <span className="text-[#E5B842]"><Hash className="w-3 h-3 inline" /> {club.wsbf_club_id}</span>
                <span><MapPin className="w-3 h-3 inline" /> {club.city ? `${club.city}, ` : ""}{club.country?.name}</span>
                {club.federation && <span><Shield className="w-3 h-3 inline" /> {club.federation.name}</span>}
                <span><Users className="w-3 h-3 inline" /> Roster {r.count}/{r.max}</span>
              </div>
            </div>
            <Link to={`/club/${club.id}`} data-testid="builder-view-public"><GhostButton className="!py-1.5 text-xs"><ExternalLink className="w-3.5 h-3.5" /> View public profile</GhostButton></Link>
          </div>
          <div className="mt-4">
            <div className="h-2 rounded-full bg-[#0a0a0a] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8A5A00] to-[#E5B842]" style={{ width: `${Math.min(100, (r.count / r.max) * 100)}%` }} /></div>
            <div className={`text-xs font-mono mt-1 ${r.eligible ? "text-emerald-300" : "text-amber-300"}`} data-testid="header-readiness">{r.eligible ? "Competition eligible roster" : `${r.min - r.count} more accepted player${r.min - r.count === 1 ? "" : "s"} required`}</div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="lg:hidden mb-4">{renderRail("m")}</div>
      <div className="grid lg:grid-cols-[13rem_minmax(0,1fr)_19rem] gap-5">
        <aside className="hidden lg:block"><div className="sticky top-20">{renderRail("d")}</div></aside>

        <main className="min-w-0">
          {section === "hq" && (
            <div data-testid="section-hq">
              <SectionHead title="Club HQ" sub="Your club’s official identity and where it stands in the WSTF lifecycle." />
              {club.status === "REJECTED" && (
                <Card className="p-4 mb-5 border-rose-500/40" data-testid="hq-rejected">
                  <div className="flex items-center gap-2 text-rose-300"><AlertTriangle className="w-4 h-4" /><span className="font-heading font-bold uppercase tracking-wide text-sm">Federation Review — Rejected</span></div>
                  {club.federation_review?.reason
                    ? <div className="text-sm text-slate-200 mt-2 break-words">{club.federation_review.reason}</div>
                    : <div className="text-sm text-slate-400 mt-2">No reason was provided by the Federation.</div>}
                  <div className="text-xs text-slate-500 mt-2">Adjust your roster (minimum {r.min} accepted players), then resubmit from the Compete tab.</div>
                </Card>
              )}
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <Metric label="Official Status" value={club.status} />
                <Metric label="Roster" value={`${r.count} / ${r.max}`} />
                <Metric label="Competition Eligible" value={club.status === "ACTIVE" ? "YES" : "NO"} />
                <Metric label="Federation" value={club.federation?.wsbf_fed_id || "—"} />
              </div>
              <IdentityEditor club={club} onReload={load} />
              <Card className="p-5 mb-5" data-testid="hq-lifecycle">
                <GroupLabel>Lifecycle</GroupLabel>
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
                  {["DRAFT", "ROSTER", "REVIEW", "ACTIVE"].map((st, i) => {
                    const reached = (st === "DRAFT") || (st === "ROSTER" && (r.eligible || ["SUBMITTED", "UNDER_REVIEW", "ACTIVE"].includes(club.status))) || (st === "REVIEW" && ["SUBMITTED", "UNDER_REVIEW", "ACTIVE"].includes(club.status)) || (st === "ACTIVE" && club.status === "ACTIVE");
                    return <span key={st} className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded uppercase tracking-wide ${reached ? "bg-[#1a1407] text-[#E5B842]" : "bg-[#141414] text-slate-600"}`}>{st === "ROSTER" ? "Build Roster" : st === "REVIEW" ? "Fed Review" : st}</span>
                      {i < 3 && <ChevronRight className="w-3 h-3 text-slate-700" />}
                    </span>;
                  })}
                </div>
                <div className="text-[10px] text-slate-500 mt-2">Build Roster &amp; Fed Review are readiness states; official statuses are DRAFT · SUBMITTED · ACTIVE · REJECTED.</div>
              </Card>
              <Card className="p-5 mb-5"><GroupLabel>Readiness checklist</GroupLabel><Readiness club={club} /></Card>
              <div className="flex flex-wrap gap-2" data-testid="hq-quick-actions">
                <GoldButton className="!py-2 text-xs" onClick={() => setSection("find")}><UserPlus className="w-4 h-4" /> Invite player</GoldButton>
                <GhostButton className="!py-2 text-xs" onClick={() => setSection("transfers")}><ArrowLeftRight className="w-4 h-4" /> View transfers</GhostButton>
                <GhostButton className="!py-2 text-xs" onClick={() => setSection("compete")}><Send className="w-4 h-4" /> Submit for review</GhostButton>
                <Link to={`/club/${club.id}`}><GhostButton className="!py-2 text-xs"><ExternalLink className="w-4 h-4" /> Public profile</GhostButton></Link>
              </div>
            </div>
          )}
          {section === "roster" && <RosterSection club={club} onSelect={(m) => setSelected(m)} />}
          {section === "find" && <FindSection club={club} invited={invited} onInvite={invite} onSelect={(p) => setSelected(p)} />}
          {section === "transfers" && <TransfersSection club={club} />}
          {section === "compete" && <CompeteSection club={club} onSubmit={submit} submitting={submitting} />}
        </main>

        <aside className="min-w-0 space-y-4">
          <Card className="p-5" data-testid="context-status">
            <GroupLabel>Club Status</GroupLabel>
            <div className="flex items-center gap-2 mb-3"><StatusChip status={club.status} /></div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <Field label="Roster" value={`${r.count}/${r.max}`} /><Field label="Minimum" value={r.min} />
            </div>
            <div className="text-[11px] uppercase tracking-wider text-[#E5B842] font-mono mb-1">Next step</div>
            <div className="text-xs text-slate-300">{step.text}</div>
          </Card>
          <PlayerPanel player={selected} club={club} invited={invited} onInvite={invite} onClose={() => setSelected(null)} />
        </aside>
      </div>
    </div>
  );
}
